import React, { useEffect, useMemo, useRef, useState } from "react";
import Barcode from "react-barcode";
import "./CSS/SareeBarcodes.css";
import { backend_url, currency } from "../App";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

/* offer price = original − discount% (same formula as the product barcode modal) */
const offerPrice = (p) => {
  const oldP = Number(p.old_price) || 0;
  const disc = Number(p.new_price) || 0;
  return (oldP - (oldP * disc) / 100).toFixed(2);
};

const SareeBarcodes = () => {
  const [sarees, setSarees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [copies, setCopies] = useState(1);
  const [showPrice, setShowPrice] = useState(true);
  const [previewNew, setPreviewNew] = useState(false);
  const [imgSize, setImgSize] = useState("m"); // s | m | l
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfProg, setPdfProg] = useState(0);
  const printRef = useRef();

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${backend_url}/allproducts`);
        const data = await res.json();
        const s = data.filter((p) => p.category?.toLowerCase() === "sarees");
        setSarees(s);
        setSelected(new Set(s.map((p) => p._id))); // select all by default
      } catch {
        toast.error("Failed to load sarees");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  /* Preview of the future AFH ids — MUST match migrate-saree-ids.js ordering exactly
     (numeric id asc, then _id), so what you see here equals what will be saved. */
  const newIdMap = useMemo(() => {
    const sorted = [...sarees].sort((a, b) => {
      const ai = Number(a.id), bi = Number(b.id);
      if (!isNaN(ai) && !isNaN(bi) && ai !== bi) return ai - bi;
      return String(a._id).localeCompare(String(b._id));
    });
    const m = {};
    sorted.forEach((p, i) => { m[p._id] = `AFH${String(i + 1).padStart(7, "0")}`; });
    return m;
  }, [sarees]);

  const displayId = (p) => (previewNew ? (newIdMap[p._id] || p.unique_id) : p.unique_id);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return sarees;
    return sarees.filter(
      (p) =>
        p.unique_id?.toLowerCase().includes(q) ||
        newIdMap[p._id]?.toLowerCase().includes(q) ||
        p.name?.toLowerCase().includes(q)
    );
  }, [sarees, search, newIdMap]);

  const selectedList = useMemo(
    () => sarees.filter((p) => selected.has(p._id)),
    [sarees, selected]
  );

  const toggle = (id) =>
    setSelected((prev) => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });

  const selectAllFiltered = () =>
    setSelected(new Set(filtered.map((p) => p._id)));
  const clearAll = () => setSelected(new Set());

  const nCopies = Math.max(1, Math.min(50, Number(copies) || 1));

  /* open a print window with the given body + styles, wait for images, then print */
  const printWindow = (title, styles, body) => {
    const win = window.open("", "_blank");
    win.document.write(`<html><head><title>${title}</title><style>
      * { box-sizing: border-box; }
      body { font-family: Arial, sans-serif; margin: 12px; background: #fff; }
      ${styles}
    </style></head><body>${body}</body></html>`);
    win.document.close();
    win.focus();

    let printed = false;
    const doPrint = () => {
      if (printed) return;
      printed = true;
      win.focus(); win.print(); win.close();
    };
    const imgs = win.document.images;
    if (!imgs || imgs.length === 0) { setTimeout(doPrint, 200); return; }
    let done = 0;
    const tick = () => { if (++done >= imgs.length) doPrint(); };
    Array.from(imgs).forEach((img) => {
      if (img.complete) tick();
      else { img.onload = tick; img.onerror = tick; }
    });
    setTimeout(doPrint, 6000); // safety fallback
  };

  const handlePrint = () => {
    if (!selectedList.length) {
      toast.info("Select at least one saree to print");
      return;
    }
    const styles = `
      .bc-sheet { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; }
      .bc-label { border: 1px dashed #bbb; border-radius: 6px; padding: 8px 6px;
        text-align: center; page-break-inside: avoid; break-inside: avoid; }
      .bc-label svg { max-width: 100%; height: auto; }
      .bc-label-img { object-fit: cover; border-radius: 4px; margin: 0 auto 4px; display: block; }
      .bc-label-price { font-size: 13px; font-weight: 700; color: #111; margin-top: 2px; }
      @media print { body { margin: 6mm; } .bc-sheet { gap: 6px; } }`;
    printWindow(`Saree Barcodes (${selectedList.length} × ${nCopies})`, styles, printRef.current.innerHTML);
  };

  /* Print the page as a catalog list — done in THIS window (reuses already-loaded
     photos, so no slow re-download of 275 images in a blank new tab). */
  const printRows = selectedList.length ? selectedList : filtered;
  const handlePrintList = () => {
    if (!printRows.length) { toast.info("No sarees to print"); return; }
    document.body.classList.add("sb-printing");
    const cleanup = () => {
      document.body.classList.remove("sb-printing");
      window.removeEventListener("afterprint", cleanup);
    };
    window.addEventListener("afterprint", cleanup);
    setTimeout(() => window.print(), 150);
  };

  /* Load an image (CORS) and downscale it to a small JPEG data URI so the PDF
     stays a reasonable size. Returns null if it can't be loaded. */
  const loadImgData = (url) =>
    new Promise((resolve) => {
      if (!url) return resolve(null);
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        try {
          const S = 220;
          const c = document.createElement("canvas");
          c.width = S; c.height = S;
          const ctx = c.getContext("2d");
          ctx.fillStyle = "#fff";
          ctx.fillRect(0, 0, S, S);
          const r = Math.max(S / img.width, S / img.height); // cover
          const w = img.width * r, h = img.height * r;
          ctx.drawImage(img, (S - w) / 2, (S - h) / 2, w, h);
          resolve(c.toDataURL("image/jpeg", 0.72));
        } catch {
          resolve(null);
        }
      };
      img.onerror = () => resolve(null);
      img.src = url;
    });

  /* Build a real PDF catalog (checkbox + photo + ID + name + price). */
  const handleDownloadPdf = async () => {
    if (!printRows.length) { toast.info("No sarees to download"); return; }
    setPdfBusy(true);
    setPdfProg(0);
    try {
      // preload + downscale all photos, with progress
      const dataMap = {};
      for (let i = 0; i < printRows.length; i++) {
        dataMap[printRows[i]._id] = await loadImgData(printRows[i].images?.[0]);
        setPdfProg(i + 1);
      }

      const { jsPDF } = await import("jspdf");
      const doc = new jsPDF({ unit: "mm", format: "a4" });
      const pageW = 210, pageH = 297, margin = 10, gap = 6;
      const colW = (pageW - 2 * margin - gap) / 2;
      const imgMM = { s: 14, m: 20, l: 28 }[imgSize] || 20;
      const rowH = imgMM + 8;
      const cols = [margin, margin + colW + gap];
      const topY = margin + 7;
      const rowsPerPage = Math.max(1, Math.floor((pageH - topY - margin) / rowH));
      const perPage = rowsPerPage * 2;

      const drawHeader = () => {
        doc.setFont("helvetica", "bold"); doc.setFontSize(14); doc.setTextColor(30, 27, 75);
        doc.text(`Saree List${previewNew ? " — New AFH IDs" : ""}`, margin, margin + 1);
        doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(120);
        doc.text(`${printRows.length} sarees · ${new Date().toLocaleDateString()}`, pageW - margin, margin + 1, { align: "right" });
        doc.setDrawColor(124, 58, 237); doc.setLineWidth(0.5);
        doc.line(margin, margin + 3.5, pageW - margin, margin + 3.5);
      };

      drawHeader();
      printRows.forEach((p, idx) => {
        const within = idx % perPage;
        if (idx > 0 && within === 0) { doc.addPage(); drawHeader(); }
        const col = within % 2;
        const row = Math.floor(within / 2);
        const x = cols[col];
        const y = topY + row * rowH;

        // checkbox
        doc.setDrawColor(60); doc.setLineWidth(0.3);
        doc.rect(x, y + 1, 4.5, 4.5);

        // photo (or placeholder)
        const data = dataMap[p._id];
        const ix = x + 7;
        if (data) doc.addImage(data, "JPEG", ix, y, imgMM, imgMM);
        else { doc.setDrawColor(220); doc.rect(ix, y, imgMM, imgMM); }

        // text block
        const tx = ix + imgMM + 3;
        const tw = x + colW - tx;
        doc.setFont("helvetica", "bold"); doc.setFontSize(11); doc.setTextColor(30, 27, 75);
        doc.text(displayId(p), tx, y + 4);
        if (previewNew) {
          doc.setFont("helvetica", "normal"); doc.setFontSize(7); doc.setTextColor(160);
          doc.text(`was ${p.unique_id}`, tx, y + 8);
        }
        doc.setFont("helvetica", "normal"); doc.setFontSize(8); doc.setTextColor(70);
        const nameLines = doc.splitTextToSize(p.name || "", tw).slice(0, 2);
        doc.text(nameLines, tx, y + (previewNew ? 12 : 9));
        if (showPrice) {
          doc.setFont("helvetica", "bold"); doc.setFontSize(9); doc.setTextColor(91, 33, 182);
          doc.text(`${currency}${offerPrice(p)}`, tx, y + imgMM);
        }
      });

      doc.save(`saree-list-${new Date().toISOString().slice(0, 10)}.pdf`);
      toast.success("PDF downloaded");
    } catch (e) {
      toast.error("Could not generate PDF");
    } finally {
      setPdfBusy(false);
    }
  };

  // labels to render (each saree repeated nCopies times)
  const labels = [];
  selectedList.forEach((p) => {
    for (let c = 0; c < nCopies; c++) labels.push({ ...p, _key: `${p._id}-${c}` });
  });

  return (
    <div className="sb-page">
      <div className="sb-header">
        <div className="sb-title-row">
          <h1 className="sb-title">Saree Barcodes</h1>
          <span className="sb-badge">{selectedList.length} selected · {labels.length} labels</span>
          <button className="sb-print-btn sb-print-list" onClick={handlePrintList}>
            📄 Print List
          </button>
          <button
            className="sb-print-btn sb-download-list"
            onClick={handleDownloadPdf}
            disabled={pdfBusy}
          >
            {pdfBusy ? `⏳ ${pdfProg}/${printRows.length}` : "⬇ Download PDF"}
          </button>
          <button className="sb-print-btn" onClick={handlePrint} disabled={!selectedList.length}>
            🖨 Print Barcodes
          </button>
        </div>
        <p className="sb-subtitle">
          Print barcode stickers (encoding each saree's ID) and paste them on the sarees.
        </p>

        <div className="sb-controls">
          <div className="sb-search-wrap">
            <span className="sb-search-icon">&#128269;</span>
            <input
              type="text"
              placeholder="Search by ID or name..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <button className="sb-ctrl-btn" onClick={selectAllFiltered}>Select all ({filtered.length})</button>
          <button className="sb-ctrl-btn" onClick={clearAll}>Clear</button>
          <label className="sb-copies">
            Copies each
            <input
              type="number"
              min="1"
              max="50"
              value={copies}
              onChange={(e) => setCopies(e.target.value)}
            />
          </label>
          <label className="sb-copies">
            Image size
            <select value={imgSize} onChange={(e) => setImgSize(e.target.value)}>
              <option value="s">Small</option>
              <option value="m">Medium</option>
              <option value="l">Large</option>
            </select>
          </label>
          <label className="sb-toggle">
            <input
              type="checkbox"
              checked={showPrice}
              onChange={(e) => setShowPrice(e.target.checked)}
            />
            Show price
          </label>
          <label className={`sb-toggle sb-toggle-preview ${previewNew ? "on" : ""}`}>
            <input
              type="checkbox"
              checked={previewNew}
              onChange={(e) => setPreviewNew(e.target.checked)}
            />
            Preview new AFH IDs
          </label>
        </div>

        {previewNew && (
          <div className="sb-preview-banner">
            👁 Previewing the <b>new AFH IDs</b> — this is exactly what will be saved when you apply.
            <b> Nothing is written to the database yet.</b> Old IDs shown in grey for reference.
          </div>
        )}
      </div>

      {/* Selectable list */}
      {loading ? (
        <div className="sb-empty">Loading sarees…</div>
      ) : (
        <div className="sb-list">
          {filtered.map((p) => (
            <label key={p._id} className={`sb-item ${selected.has(p._id) ? "on" : ""}`}>
              <input
                type="checkbox"
                checked={selected.has(p._id)}
                onChange={() => toggle(p._id)}
              />
              <img src={p.images?.[0]} alt={p.name} className="sb-item-thumb" />
              <div className="sb-item-info">
                <span className="sb-item-id">
                  {displayId(p)}
                  {previewNew && (
                    <span className="sb-item-oldid">was {p.unique_id}</span>
                  )}
                </span>
                <span className="sb-item-name">{p.name}</span>
              </div>
            </label>
          ))}
          {filtered.length === 0 && <div className="sb-empty">No sarees match your search</div>}
        </div>
      )}

      {/* Print-only catalog (rendered in-page; visible only when printing the list) */}
      <div className={`sb-print-catalog size-${imgSize}`}>
        <div className="sb-cat-head">
          <h1>Saree List{previewNew ? " — New AFH IDs" : ""}</h1>
          <span>{printRows.length} sarees · {new Date().toLocaleDateString()}</span>
        </div>
        <div className="sb-cat-grid">
          {printRows.map((p) => (
            <div className="sb-cat-item" key={p._id}>
              <span className="sb-cat-check" />
              <img className="sb-cat-img" src={p.images?.[0]} alt="" />
              <div className="sb-cat-info">
                <div className="sb-cat-id">
                  {displayId(p)}
                  {previewNew && <span className="sb-cat-old">was {p.unique_id}</span>}
                </div>
                <div className="sb-cat-name">{p.name}</div>
                {showPrice && <div className="sb-cat-price">{currency}{offerPrice(p)}</div>}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Hidden printable sheet */}
      <div style={{ position: "absolute", left: "-99999px", top: 0 }} aria-hidden="true">
        <div className="bc-sheet" ref={printRef}>
          {labels.map((p) => (
            <div className="bc-label" key={p._key}>
              <Barcode
                value={displayId(p) || "NA"}
                width={1.4}
                height={45}
                fontSize={11}
                margin={4}
                displayValue={true}
                background="#ffffff"
                lineColor="#000000"
              />
              {showPrice && (
                <div className="bc-label-price">{currency}{offerPrice(p)}</div>
              )}
            </div>
          ))}
        </div>
      </div>

      <ToastContainer />
    </div>
  );
};

export default SareeBarcodes;
